import React from 'react';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function TermsOfServicePage() {
  return (
    <>
      <SEOHead {...seoConfigs.terms} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Header */}
        <div className="bg-[#e4fde1] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Terms of Service
            </h1>
            <p className="text-2xl text-[#254e70]/80 mb-4 max-w-4xl mx-auto">
              Please read these terms carefully before using TherapyList
            </p>
            <p className="text-lg text-[#254e70]/60">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <div className="prose prose-lg max-w-none">

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    1. Acceptance of Terms
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Welcome to TherapyList. These Terms of Service ("Terms") govern your use of the TherapyList website and platform (the "Service") operated by TherapyList ("we," "our," or "us"). By accessing or using our Service, you agree to be bound by these Terms.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    If you disagree with any part of these Terms, then you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    2. Description of Service
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TherapyList is a platform that connects individuals seeking mental health services with licensed therapists and mental health professionals. Our Service includes:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>A directory of licensed mental health professionals</li>
                    <li>Search and filtering tools to find appropriate therapists</li>
                    <li>Communication tools to facilitate initial contact</li>
                    <li>Information about therapist specialties, credentials, and services</li>
                    <li>Resources and educational content about mental health</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Important:</strong> TherapyList is not a healthcare provider and does not provide medical or therapeutic services. We facilitate connections between clients and independent therapists.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    3. User Accounts and Registration
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To access certain features of our Service, you may be required to create an account. When you create an account, you must:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Provide accurate, complete, and up-to-date information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Be at least 18 years old or have parental consent</li>
                    <li>Use the Service only for lawful purposes</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    You are responsible for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    4. Therapist Verification and Credentials
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We make reasonable efforts to verify that therapists listed on our platform are licensed mental health professionals. However:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>We are not responsible for the accuracy of therapist credentials</li>
                    <li>Users should independently verify therapist licensing and qualifications</li>
                    <li>We do not supervise or monitor therapy sessions</li>
                    <li>The therapeutic relationship is solely between the client and therapist</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    It is your responsibility to ensure that any therapist you choose to work with is appropriately licensed and qualified for your needs.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    5. User Conduct
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    You agree not to use the Service to:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Impersonate any person or entity</li>
                    <li>Submit false or misleading information</li>
                    <li>Harass, threaten, or abuse other users or therapists</li>
                    <li>Spam or send unsolicited communications</li>
                    <li>Upload malicious code or attempt to hack the platform</li>
                    <li>Use automated systems to access the Service</li>
                    <li>Interfere with the proper functioning of the Service</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    6. Privacy and Confidentiality
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy. Please note:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>TherapyList is not bound by HIPAA as we are not a healthcare provider</li>
                    <li>Communications through our platform are secure but not privileged</li>
                    <li>Therapist-client privilege begins when the therapeutic relationship starts</li>
                    <li>We may disclose information if required by law or to prevent harm</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    7. Payments and Fees
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TherapyList may charge fees for certain features or services. All fees are non-refundable unless otherwise stated. Key points:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Therapy session fees are paid directly to therapists</li>
                    <li>Platform fees, if any, will be clearly disclosed</li>
                    <li>You are responsible for any applicable taxes</li>
                    <li>We reserve the right to change fees with notice</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    8. Intellectual Property
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The Service and its original content, features, and functionality are owned by TherapyList and are protected by copyright, trademark, and other intellectual property laws.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    You may not reproduce, distribute, modify, or create derivative works of any part of the Service without our express written permission.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    9. Disclaimers and Limitations
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Medical Disclaimer</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TherapyList is not a medical or healthcare provider. We do not provide medical advice, diagnosis, or treatment. The information on our platform is for informational purposes only and should not replace professional medical advice.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Emergency Situations</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <strong>IMPORTANT:</strong> If you are experiencing a mental health emergency or having thoughts of self-harm, please contact emergency services immediately (911 in the US) or go to your nearest emergency room. TherapyList is not equipped to handle emergency situations.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Platform Results</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    While TherapyList strives to connect therapists with potential clients, we cannot guarantee specific outcomes regarding client inquiries, appointment bookings, or business success for therapists using our platform. Results may vary based on factors including location, specialties, availability, profile completeness, and market demand.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Service Availability</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We strive to maintain the availability of our Service but cannot guarantee uninterrupted access. The Service is provided "as is" without warranties of any kind.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    10. Limitation of Liability
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To the fullest extent permitted by law, TherapyList shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Loss of profits, data, or business opportunities</li>
                    <li>Personal injury or property damage</li>
                    <li>Actions or omissions of therapists or other users</li>
                    <li>Interruption or termination of services</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    11. Indemnification
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    You agree to indemnify and hold harmless TherapyList and its affiliates from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    12. Termination
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We may terminate or suspend your account and access to the Service immediately, without prior notice, for any breach of these Terms. You may also terminate your account at any time.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Upon termination, your right to use the Service will cease immediately, but certain provisions of these Terms will survive termination.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    13. Governing Law and Dispute Resolution
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    These Terms are governed by the laws of [State/Jurisdiction], without regard to conflict of law principles. Any disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    14. Changes to Terms
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the new Terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    15. Contact Information
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <div className="bg-[#e4fde1] p-6 rounded-lg">
                    <p className="text-gray-700 mb-2"><strong>TherapyList</strong></p>
                    <p className="text-gray-700">Email: help@therapylist.com</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TermsOfServicePage; 